import posTerminalFields from '@/app/modules/terminals/fields/posTerminalFields';
import POS_TERMINAL_STATUS from '@/app/modules/terminals/enums/PosTerminalStatus';

import subscriptionFields from '../../../../subscriptions/fields/subscriptionFields';
import SUBSCRIPTION_STATUS from '../../../../subscriptions/enums/SubscriptionStatus';

import ICONS from '@/common-utils/enums/icons';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import Permissions from '@/app/permissions/Permissions';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
import getObjectAttributeFromStringKey from '@/common-utils/functions/getObjectAttributeFromStringKey';
import { DateTime } from 'luxon';
import { useToast } from '@/common-utils/hooks/use-toast';
import Entity from '@/common-utils/components/renders/entity/Entity';
import useSubscriptionStore from '@/app/store/modules/subscription';
import { nsTerminal } from '@/app/i18n/namespaces';
import actionsPosTerminals from '../../../api/actionsPosTerminals';

/**
 * @param subscription
 * @param permissions
 * @param root0
 * @param root0.handleShowModalTemporarilyAllow
 * @param root0.handleShowModalBlacklistPosTerminal
 * @param root0.handleShowModalUnblacklistPosTerminal
 * @param root0.handleShowModalRewarehousePosTerminal
 * @param root0.handleShowModalScheduledBlacklistPosTerminal
 * @returns {JSX} GenerateActionsPosTerminalLifecycle
 */
function GenerateActionsPosTerminalLifecycle(subscription, permissions,
    {
        handleShowModalTemporarilyAllow,
        handleShowModalBlacklistPosTerminal,
        handleShowModalUnblacklistPosTerminal,
        handleShowModalRewarehousePosTerminal,
        handleShowModalScheduledBlacklistPosTerminal
    }) {

    // Toast
    const { toast } = useToast();

    // Store
    const storeRefetchSubscriptions = useSubscriptionStore(s => s.storeRefetchSubscriptions);

    // API Calls
    const submitBlacklistPosTerminal = actionsPosTerminals.blacklistPosTerminal(() => { }, () => { });
    const submitUnblacklistPosTerminal = actionsPosTerminals.unblacklistPosTerminal(() => { }, () => { });

    // Functions
    const refreshPosTerminal = async () => {
        toast({ description: <Entity entity='toast.refresh.loading' namespace={nsTerminal} /> });
        const subscriptionId = subscription[subscriptionFields.UUID.key];
        const posTerminalStatus = getObjectAttributeFromStringKey(subscription, generateNestedFieldAccessor(subscriptionFields.POS_TERMINAL.STATUS));
        const BLACKLIST_START_DATE = DateTime.now();

        if (posTerminalStatus === POS_TERMINAL_STATUS.ALLOWED.name) {
            try {
                await submitBlacklistPosTerminal.mutateAsync({ subscriptionId, blacklistStartDate: BLACKLIST_START_DATE });
                await submitUnblacklistPosTerminal.mutateAsync({ subscriptionId });
            }
            // eslint-disable-next-line no-unused-vars
            catch (error) {
                return (
                    toast({
                        description: <Entity entity='toast.refresh.error' namespace={nsTerminal} />,
                        variant: 'danger'
                    })
                );
            }
        }
        else if (posTerminalStatus === POS_TERMINAL_STATUS.BLOCKED.name) {
            try {
                await submitUnblacklistPosTerminal.mutateAsync({ subscriptionId });
                await submitBlacklistPosTerminal.mutateAsync({ subscriptionId, blacklistStartDate: BLACKLIST_START_DATE });
            }
            // eslint-disable-next-line no-unused-vars
            catch (error) {
                return (
                    toast({
                        description: <Entity entity='toast.refresh.error' namespace={nsTerminal} />,
                        variant: 'danger'
                    })
                );
            }
        }
        return (
            storeRefetchSubscriptions(true),
            toast({
                description: <Entity entity='toast.refresh.success' namespace={nsTerminal} />,
                variant: 'success'
            })
        );
    };

    const _posTerminalStatusIsOneOf = (statuses) => {
        // Abort the case where the terminal is not loaded yet or where the statuses are invalid
        if (!subscription.posTerminal || !statuses) {
            return false;
        }
        const posTerminalStatus = subscription.posTerminal[posTerminalFields.STATUS.key];

        return statuses.some(status => posTerminalStatus === status.name);
    };
    const _subscriptionStatusIsOneOf = (subscriptionStatuses) => {
        // Abort the case where the subscription is not loaded yet or where the statuses are invalid
        if (!subscription || !subscriptionStatuses) {
            return false;
        }
        const subscriptionStatus = subscription[subscriptionFields.STATUS.key];

        return subscriptionStatuses.some(status => subscriptionStatus === status.name);
    };

    const _subscriptionStatusIsNotOneOf = (subscriptionStatuses) => {
        // Abort the case where the subscription is not loaded yet or where the statuses are invalid
        if (!subscription || !subscriptionStatuses) {
            return false;
        }
        const subscriptionStatus = subscription[subscriptionFields.STATUS.key];

        return subscriptionStatuses.some(status => subscriptionStatus !== status.name);
    };

    return (
        [
            {
                icon: ICONS.POS_TERMINAL_SCHEDULED_TERMINATE,
                entity: 'actions.row.scheduled_block',
                namespace: nsTerminal,
                className: 'text-danger',
                onClick: handleShowModalScheduledBlacklistPosTerminal,
                visible: _subscriptionStatusIsNotOneOf([SUBSCRIPTION_STATUS.IN_STOCK]) &&
                    _posTerminalStatusIsOneOf([POS_TERMINAL_STATUS.TEMPORARILY_ALLOWED, POS_TERMINAL_STATUS.ALLOWED]),
                disabled: !hasRequiredPermissions({
                    permissions,
                    requiredPermissions: [Permissions.BLACKLIST_ADD_ENTRY.name, Permissions.SUBSCRIPTION_CONNECTION_POINTS_READ.name]
                }),
                tooltipForbidden: 'title.permissions.access_denied'
            },
            {
                icon: ICONS.POS_TERMINAL_TEMPORARY_ACTIVATE,
                entity: 'actions.row.temporarily_allow',
                namespace: nsTerminal,
                className: 'text-warning',
                onClick: handleShowModalTemporarilyAllow,
                visible: _subscriptionStatusIsOneOf([SUBSCRIPTION_STATUS.TERMINATED, SUBSCRIPTION_STATUS.TO_BE_ACTIVATED]) &&
                    _posTerminalStatusIsOneOf([POS_TERMINAL_STATUS.BLOCKED]),
                disabled: !hasRequiredPermissions({
                    permissions,
                    requiredPermissions: [Permissions.BLACKLIST_ADD_ENTRY.name, Permissions.SUBSCRIPTION_CONNECTION_POINTS_READ.name]
                }),
                tooltipForbidden: 'title.permissions.access_denied'
            },
            {
                icon: ICONS.POS_TERMINAL_ACTIVATE,
                entity: 'actions.row.allow',
                namespace: nsTerminal,
                className: 'text-success',
                onClick: handleShowModalUnblacklistPosTerminal,
                visible: _subscriptionStatusIsOneOf([SUBSCRIPTION_STATUS.ACTIVATED, SUBSCRIPTION_STATUS.IN_STOCK]) &&
                    _posTerminalStatusIsOneOf([POS_TERMINAL_STATUS.BLOCKED, POS_TERMINAL_STATUS.TEMPORARILY_ALLOWED]),
                disabled: !hasRequiredPermissions({
                    permissions,
                    requiredPermissions: [Permissions.BLACKLIST_REMOVE_ENTRY.name, Permissions.SUBSCRIPTION_CONNECTION_POINTS_READ.name]
                }),
                tooltipForbidden: 'title.permissions.access_denied'
            },
            {
                icon: ICONS.POS_TERMINAL_TERMINATE,
                entity: 'actions.row.block',
                namespace: nsTerminal,
                className: 'text-danger',
                variant: 'critical',
                onClick: handleShowModalBlacklistPosTerminal,
                visible: _subscriptionStatusIsNotOneOf([SUBSCRIPTION_STATUS.IN_STOCK]) &&
                    _posTerminalStatusIsOneOf([POS_TERMINAL_STATUS.TEMPORARILY_ALLOWED, POS_TERMINAL_STATUS.ALLOWED]),
                disabled: !hasRequiredPermissions({
                    permissions,
                    requiredPermissions: [Permissions.BLACKLIST_ADD_ENTRY.name, Permissions.SUBSCRIPTION_CONNECTION_POINTS_READ.name]
                }),
                tooltipForbidden: 'title.permissions.access_denied'
            },
            {
                icon: ICONS.IN_STOCK,
                entity: 'actions.row.rewarehouse',
                namespace: nsTerminal,
                className: 'text-neutral',
                onClick: handleShowModalRewarehousePosTerminal,
                visible: subscription.posTerminal && _subscriptionStatusIsNotOneOf([SUBSCRIPTION_STATUS.IN_STOCK]),
                disabled: !hasRequiredPermissions({ permissions, requiredPermissions: [Permissions.SUBSCRIPTION_REWAREHOUSE.name] }),
                tooltipForbidden: 'title.permissions.access_denied'

            },
            {
                icon: ICONS.REFRESH,
                entity: 'actions.row.refresh',
                namespace: nsTerminal,
                className: 'text-warning',
                onClick: refreshPosTerminal,
                visible: ((_subscriptionStatusIsOneOf([SUBSCRIPTION_STATUS.ACTIVATED])
                    && _posTerminalStatusIsOneOf([POS_TERMINAL_STATUS.ALLOWED, POS_TERMINAL_STATUS.BLOCKED]))
                    || (_subscriptionStatusIsOneOf([SUBSCRIPTION_STATUS.IN_STOCK]) &&
                        _posTerminalStatusIsOneOf([POS_TERMINAL_STATUS.ALLOWED]))),
                disabled: !hasRequiredPermissions({
                    permissions,
                    requiredPermissions: [Permissions.BLACKLIST_ADD_ENTRY.name, Permissions.BLACKLIST_REMOVE_ENTRY.name]
                }),
                tooltipForbidden: 'title.permissions.access_denied'
            }
        ]
    );
}

export default GenerateActionsPosTerminalLifecycle;